import { DOCUMENT } from '@angular/common';
import { Injectable, inject } from '@angular/core';
import { ConfigStateService } from '@abp/ng.core';
import {
  BehaviorSubject,
  EMPTY,
  Subject,
  catchError,
  concatMap,
  distinctUntilChanged,
  tap,
} from 'rxjs';
import { UiColorTheme } from 'src/core/models/theme/ui-color-theme.enum';
import { UiSettingsService } from 'src/core/services/settings/ui-settings.service';
import { UiColorThemePreset } from 'src/core/models/theme/ui-color-theme-preset.model';
import {
  DEFAULT_UI_COLOR_THEME,
  UI_COLOR_THEME_PRESETS,
  UI_COLOR_THEME_SETTING_NAME,
} from 'src/core/constants/theme/ui-color-theme.constants';

@Injectable({ providedIn: 'root' })
export class UiColorThemeService {
  private readonly document = inject(DOCUMENT);
  private readonly configState = inject(ConfigStateService);
  private readonly uiSettings = inject(UiSettingsService);

  private readonly selectedSubject = new BehaviorSubject<UiColorTheme>(DEFAULT_UI_COLOR_THEME);
  private readonly selectionRequests = new Subject<UiColorTheme>();
  private appliedTheme: UiColorTheme | undefined;
  private confirmedTheme = DEFAULT_UI_COLOR_THEME;
  readonly selected$ = this.selectedSubject.pipe(distinctUntilChanged());

  get presets(): readonly UiColorThemePreset[] {
    return UI_COLOR_THEME_PRESETS;
  }

  constructor() {
    this.selectionRequests
      .pipe(
        concatMap(theme =>
          this.uiSettings.updateColorTheme({ colorTheme: theme }).pipe(
            tap(() => {
              this.confirmedTheme = theme;
              this.apply(theme);
            }),
            catchError(() => {
              this.apply(this.confirmedTheme);
              return EMPTY;
            }),
          ),
        ),
      )
      .subscribe();
  }

  initialize(): void {
    this.configState
      .getSetting$(UI_COLOR_THEME_SETTING_NAME)
      .subscribe(storedName => this.applyStoredName(storedName));
  }

  select(theme: UiColorTheme): void {
    if (this.selectedSubject.value === theme) {
      return;
    }

    this.apply(theme);
    this.selectionRequests.next(theme);
  }

  private applyStoredName(storedName: string | undefined): void {
    const stored = UI_COLOR_THEME_PRESETS.find(preset => UiColorTheme[preset.theme] === storedName);
    const theme = stored?.theme ?? DEFAULT_UI_COLOR_THEME;
    this.confirmedTheme = theme;
    this.apply(theme);
  }

  private apply(theme: UiColorTheme): void {
    if (this.appliedTheme === theme) {
      return;
    }

    const preset = UI_COLOR_THEME_PRESETS.find(candidate => candidate.theme === theme);
    if (!preset) {
      return;
    }
    const classList = this.document.documentElement.classList;
    UI_COLOR_THEME_PRESETS.forEach(candidate => classList.remove(candidate.rootClass));
    classList.add(preset.rootClass);

    this.appliedTheme = preset.theme;
    this.selectedSubject.next(preset.theme);
  }
}
